import { Loader } from 'components/shared/Loader';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const KeywordBox = ({
  combinedKeywords,
  clientFreqV2 = [],
  handleKeywordChange,
  autoKeywordsGenerationLoading,
  isReadOnly = false,
  onEditingChange,
  listHeight,
}) => {
  const { t } = useTranslation();
  const normalizeKeyword = (value) =>
    String(value || '')
      .replace(/\s*\(\s*\d+\s*\)\s*$/, '')
      .trim();

  const isValidKeywordLabel = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw || raw === 'undefined' || raw === 'null' || raw === 'undefined(undefined)') {
      return false;
    }
    const normalized = normalizeKeyword(value);
    if (!normalized) return false;
    const lower = normalized.toLowerCase();
    return lower !== 'undefined' && lower !== 'null';
  };

  const parseTokens = (value) => {
    const source = String(value || '').trim();
    if (!source) return [];


    const frequencyChunks = source.match(/[^\n,;|\t]+?\(\s*\d+\s*\)/g);
    if (frequencyChunks && frequencyChunks.length > 1) {
      return frequencyChunks.map((token) => token.trim()).filter(Boolean);
    }

    return source
      .split(/[\n,;|\t]+/)
      .map((token) => token.trim())
      .filter(Boolean);
  };

  const extractFrequency = (token) => {
    const match = String(token || '').trim().match(/^(.+?)\((\d+)\)$/);
    if (!match) return null;
    return {
      name: normalizeKeyword(match[1]),
      frequency: parseInt(match[2], 10),
    };
  };

  const [keywords, setKeywords] = useState(() =>
    parseTokens(combinedKeywords).map(normalizeKeyword).filter(isValidKeywordLabel)
  );
  const [inputValue, setInputValue] = useState('');
  const [insertIndex, setInsertIndex] = useState(null);
  const [editedTotals, setEditedTotals] = useState({});
  const [editedKeywords, setEditedKeywords] = useState({});
  const [pendingRemoved, setPendingRemoved] = useState(() => new Set());
  const [editingKeywordIndex, setEditingKeywordIndex] = useState(null);
  const [newKeywordDraft, setNewKeywordDraft] = useState(null);
  const ignoreKeywordCommitRef = useRef(false);

  useEffect(() => {
    if (typeof onEditingChange !== 'function') return undefined;
    const isEditingNow =
      Number.isInteger(editingKeywordIndex) ||
      Boolean(newKeywordDraft) ||
      Boolean(String(inputValue || '').trim());
    onEditingChange(isEditingNow);
    return () => onEditingChange(false);
  }, [editingKeywordIndex, newKeywordDraft, inputValue, onEditingChange]);

  useEffect(() => {
    if (newKeywordDraft) return undefined;

    const fromCombined = parseTokens(combinedKeywords).map(normalizeKeyword).filter(isValidKeywordLabel);
    const fromClientFreq = (Array.isArray(clientFreqV2) ? clientFreqV2 : [])
      .map((item) => normalizeKeyword(item?.name))
      .filter(isValidKeywordLabel);

    const merged = [...fromCombined];
    const seen = new Set(fromCombined.map((item) => item.toLowerCase()));
    fromClientFreq.forEach((item) => {
      const key = item.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(item);
      }
    });

    const colorRankByKeyword = new Map(
      (Array.isArray(clientFreqV2) ? clientFreqV2 : []).map((item) => {
        const key = normalizeKeyword(item?.name).toLowerCase();
        const color = item?.color;
        const rank = color === 'green' ? 2 : color === 'yellow' ? 1 : 0;
        return [key, rank];
      })
    );

    const filtered = merged
      .filter((item) => !pendingRemoved.has(item.toLowerCase()))
      .sort((a, b) => {
        const rankA = colorRankByKeyword.get(a.toLowerCase()) ?? 0;
        const rankB = colorRankByKeyword.get(b.toLowerCase()) ?? 0;
        if (rankA !== rankB) return rankA - rankB;
        return a.localeCompare(b, undefined, { sensitivity: 'base' });
      });
    setKeywords(filtered);

    const sourceKeys = new Set(merged.map((item) => item.toLowerCase()));
    setPendingRemoved((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      prev.forEach((key) => {
        if (!sourceKeys.has(key)) {
          next.delete(key);
        }
      });
      return next.size === prev.size ? prev : next;
    });
  }, [combinedKeywords, clientFreqV2, pendingRemoved, newKeywordDraft]);

  useEffect(() => {
    setEditedKeywords(() => {
      const next = {};
      keywords.forEach((keyword, index) => {
        next[index] = keyword;
      });
      return next;
    });
  }, [keywords]);

  const freqMap = new Map(
    (Array.isArray(clientFreqV2) ? clientFreqV2 : [])
      .map((item) => [normalizeKeyword(item?.name).toLowerCase(), item])
  );

  const getColorPalette = (color) => {
    if (color === 'green') {
      return { background: '#e9f8ea', border: '#bceac2', text: '#1f7a2e' };
    }
    if (color === 'yellow') {
      return { background: '#fff6db', border: '#f4d88f', text: '#8a6200' };
    }
    return { background: '#fde8e8', border: '#f7b3b3', text: '#b42318' };
  };

  const legacyFreqMap = new Map(
    parseTokens(combinedKeywords)
      .map(extractFrequency)
      .filter(Boolean)
      .map((item) => [item.name.toLowerCase(), item.frequency])
  );

  useEffect(() => {
    setEditedTotals((prev) => {
      const next = { ...prev };
      keywords.forEach((keyword) => {
        const key = keyword.toLowerCase();
        if (next[key] !== undefined) return;
        const legacyFrequency = legacyFreqMap.get(key);
        const apiTotal = Number(freqMap.get(key)?.total);
        next[key] = Number.isFinite(Number(legacyFrequency))
          ? Number(legacyFrequency)
          : Number.isFinite(apiTotal)
            ? apiTotal
            : 1;
      });

      Object.keys(next).forEach((key) => {
        if (!keywords.some((k) => k.toLowerCase() === key)) {
          delete next[key];
        }
      });
      return next;
    });
  }, [keywords, combinedKeywords, clientFreqV2]);

  const emitKeywords = (nextKeywords, totalOverrides = {}) => {
    if (typeof handleKeywordChange !== 'function') return;
    const payload = nextKeywords
      .filter((keyword) => normalizeKeyword(keyword))
      .map((keyword) => {
        const key = keyword.toLowerCase();
        const legacyFrequency = legacyFreqMap.get(key);
        const apiTotal = Number(freqMap.get(key)?.total);
        const overrideFrequency = totalOverrides[key];
        const frequency = Number.isFinite(Number(overrideFrequency))
          ? Number(overrideFrequency)
          : Number.isFinite(Number(legacyFrequency))
            ? Number(legacyFrequency)
            : Number.isFinite(apiTotal)
              ? apiTotal
              : 1;
        return `${keyword}(${frequency})`;
      })
      .join(',\n');
    handleKeywordChange({ target: { value: payload } });
  };

  const addKeywords = (raw, baseKeywords) => {
    const sourceKeywords = (Array.isArray(baseKeywords) ? baseKeywords : keywords).filter((keyword) =>
      normalizeKeyword(keyword)
    );
    const parsedCandidates = parseTokens(raw)
      .map((token) => {
        const freq = extractFrequency(token);
        if (freq) {
          return { keyword: normalizeKeyword(freq.name), total: Number(freq.frequency) };
        }
        return { keyword: normalizeKeyword(token), total: null };
      })
      .filter((item) => item.keyword);

    const candidates = parsedCandidates.map((item) => item.keyword);
    if (!candidates.length) return;
    const existing = new Set(sourceKeywords.map((k) => k.toLowerCase()));
    const safeInsertIndex = Number.isInteger(insertIndex)
      ? Math.min(Math.max(insertIndex, 0), sourceKeywords.length)
      : sourceKeywords.length;
    const next = [...sourceKeywords];
    let cursor = safeInsertIndex;
    const totalOverrides = {};
    candidates.forEach((candidate) => {
      const key = candidate.toLowerCase();
      if (!existing.has(key)) {
        existing.add(key);
        next.splice(cursor, 0, candidate);
        cursor += 1;
      }
      const parsed = parsedCandidates.find((item) => item.keyword.toLowerCase() === key);
      if (parsed && Number.isFinite(Number(parsed.total))) {
        totalOverrides[key] = Number(parsed.total);
      }
    });

    if (Object.keys(totalOverrides).length > 0) {
      setEditedTotals((prev) => ({ ...prev, ...totalOverrides }));
    }
    setPendingRemoved((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      candidates.forEach((candidate) => next.delete(candidate.toLowerCase()));
      return next.size === prev.size ? prev : next;
    });
    setInsertIndex(cursor);
    emitKeywords(next, totalOverrides);
    setInputValue('');
  };

  const removeKeyword = (index) => {
    const removedKeyword = keywords[index] || '';
    const removedKey = removedKeyword.toLowerCase();
    const next = keywords.filter((_, i) => i !== index);
    setKeywords(next);
    setInsertIndex(null);
    setEditingKeywordIndex((prev) => {
      if (!Number.isInteger(prev)) return null;
      if (prev === index) return null;
      if (prev > index) return prev - 1;
      return prev;
    });
    setNewKeywordDraft((prev) => {
      if (!prev || !Number.isInteger(prev.index)) return null;
      if (prev.index === index) return null;
      if (prev.index > index) return { ...prev, index: prev.index - 1 };
      return prev;
    });
    setPendingRemoved((prev) => {
      const nextRemoved = new Set(prev);
      if (removedKey) nextRemoved.add(removedKey);
      return nextRemoved;
    });
    setEditedTotals((prev) => {
      const nextTotals = { ...prev };
      delete nextTotals[removedKey];
      return nextTotals;
    });
    emitKeywords(next);
  };

  const clearKeywords = () => {
    const removedKeys = keywords.map((keyword) => keyword.toLowerCase()).filter(Boolean);
    setKeywords([]);
    setInsertIndex(null);
    setEditingKeywordIndex(null);
    setNewKeywordDraft(null);
    setInputValue('');
    setEditedTotals({});
    setPendingRemoved((prev) => {
      const nextRemoved = new Set(prev);
      removedKeys.forEach((key) => nextRemoved.add(key));
      return nextRemoved;
    });
    emitKeywords([]);
  };

  const updateKeywordDraft = (index, value) => {
    setEditedKeywords((prev) => ({ ...prev, [index]: value }));
  };

  const commitKeywordRename = (index) => {
    if (ignoreKeywordCommitRef.current) {
      ignoreKeywordCommitRef.current = false;
      return;
    }
    const currentKeyword = keywords[index] || '';
    const draftValue = editedKeywords[index];
    const isNewDraft = newKeywordDraft?.index === index;

    if (isNewDraft) {
      const typed = String(draftValue ?? '').trim();
      if (!typed || !normalizeKeyword(typed)) {
        setEditedKeywords((prev) => ({ ...prev, [index]: '' }));
        setEditingKeywordIndex(index);
        return;
      }
      ignoreKeywordCommitRef.current = true;
      setNewKeywordDraft(null);
      setEditingKeywordIndex(null);
      addKeywords(typed, keywords.filter((_, idx) => idx !== index));
      return;
    }

    const normalized = normalizeKeyword(draftValue);

    if (!normalized) {
      setEditedKeywords((prev) => ({ ...prev, [index]: currentKeyword }));
      setEditingKeywordIndex(null);
      return;
    }

    const existsElsewhere = keywords.some(
      (item, idx) => idx !== index && item.toLowerCase() === normalized.toLowerCase()
    );
    if (existsElsewhere) {
      setEditedKeywords((prev) => ({ ...prev, [index]: currentKeyword }));
      setEditingKeywordIndex(index);
      return;
    }

    const nextKeywords = keywords.map((item, idx) => (idx === index ? normalized : item));
    const normalizedKey = normalized.toLowerCase();
    const totalOverrides = {};
    setKeywords(nextKeywords);
    setEditedTotals((prev) => {
      const oldKey = currentKeyword.toLowerCase();
      const newKey = normalized.toLowerCase();
      const next = { ...prev };
      if (oldKey !== newKey && next[oldKey] !== undefined && next[newKey] === undefined) {
        next[newKey] = next[oldKey];
      }
      if (oldKey !== newKey) {
        delete next[oldKey];
      }
      if (Object.prototype.hasOwnProperty.call(totalOverrides, normalizedKey)) {
        next[normalizedKey] = totalOverrides[normalizedKey];
      }
      return next;
    });
    setEditingKeywordIndex(null);
    emitKeywords(nextKeywords, totalOverrides);
  };

  const updateKeywordTotal = (keyword, rawValue) => {
    const key = keyword.toLowerCase();
    if (!/^\d+$/.test(rawValue)) return;
    const parsed = Math.max(1, Number(rawValue));
    setEditedTotals((prev) => ({ ...prev, [key]: parsed }));
    emitKeywords(keywords, { [key]: parsed });
  };

  const changeKeywordTotal = (index, delta) => {
    const keyword = keywords[index] || '';
    const key = keyword.toLowerCase();
    const isNewDraft = newKeywordDraft?.index === index;
    if (isNewDraft) {
      setNewKeywordDraft((prev) => {
        if (!prev || prev.index !== index) return prev;
        const nextTotal = Math.max(1, (Number(prev.total) || 1) + delta);
        return { ...prev, total: nextTotal, isManual: true };
      });
      return;
    }

    const match = freqMap.get(key);
    const currentTotal = Number(editedTotals[key]);
    const fallbackTotal = Number(match?.total);
    const resolved = Number.isFinite(currentTotal)
      ? currentTotal
      : Number.isFinite(fallbackTotal)
        ? fallbackTotal
        : 1;
    const nextTotal = Math.max(1, resolved + delta);
    updateKeywordTotal(keyword, String(nextTotal));
  };

   const isBulkKeywordPaste = (text) => parseTokens(text).length > 1;

  const handleKeywordPaste = (event, draftIndex) => {
    if (isReadOnly) return;
     const text = event.clipboardData?.getData('text') ?? '';
    if (!isBulkKeywordPaste(text)) return;

    event.preventDefault();
    event.stopPropagation();

    let baseKeywords = keywords;
    const isNewEmptyDraft =
      Number.isInteger(draftIndex) && newKeywordDraft?.index === draftIndex;

    if (isNewEmptyDraft) {
       const typedKeyword = normalizeKeyword(editedKeywords[draftIndex] ?? keywords[draftIndex]);
      baseKeywords = keywords.filter((_, index) => index !== draftIndex);
      if (typedKeyword) baseKeywords = [...baseKeywords, typedKeyword];
      ignoreKeywordCommitRef.current = true;
      setNewKeywordDraft(null);
      setEditingKeywordIndex(null);
    }

    addKeywords(text, baseKeywords);
  };

  const addNewKeywordBubble = () => {
    if (isReadOnly || newKeywordDraft) return;
     ignoreKeywordCommitRef.current = false;
    const next = [...keywords, ''];
    const index = next.length - 1;
    setKeywords(next);
    setInsertIndex(null);
    setNewKeywordDraft({ index, total: 1, isManual: false });
    setEditedKeywords((prev) => ({ ...prev, [index]: '' }));
    setEditingKeywordIndex(index);
  };

  const renderInsertInput = (position) => {
    if (isReadOnly) return null;
    const isEmptyListField = keywords.length === 0 && position === 0;
    const isActiveInsert = Number.isInteger(insertIndex) && insertIndex === position;
    if (!isActiveInsert && !isEmptyListField) return null;
    const isInlineBetweenTags = keywords.length > 0;

    return (
      <input
        key={`insert-${position}`}
        type="text"
        autoFocus={isActiveInsert}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={() => addKeywords(inputValue)}
        onPaste={(e) => handleKeywordPaste(e)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addKeywords(inputValue);
          }
          if (e.key === 'Backspace' && !inputValue && keywords.length > 0) {
            const removeAt = Math.max(0, position - 1);
            removeKeyword(removeAt);
          }
        }}
        placeholder={
          keywords.length === 0
            ? t('coller le gap ici')
            : t('Coller le gap')
        }
        title={t('keyword1 (2), keyword2 (1), keyword3 (1)')}
        style={{
          border: 'none',
          outline: 'none',
          background: 'transparent',
          minWidth: isInlineBetweenTags ? (inputValue ? '1ch' : '14ch') : '180px',
          width: isInlineBetweenTags
            ? `${Math.max(inputValue ? 1 : 14, inputValue.length + 1)}ch`
            : '100%',
          flex: isInlineBetweenTags ? '0 0 auto' : '1 0 120px',
          fontSize: '14px',
          padding: 0,
          margin: 0,
        }}
      />
    );
  };

  return (
    <div className="d-flex flex-column justify-content-end">
      <div className="box-wrapper">
        {autoKeywordsGenerationLoading ? (
          <Loader />
        ) : (
          <div
            className="col-12 box-list-text"
            onMouseDown={(e) => {
              if (isReadOnly) return;
              const interactiveTarget = e.target.closest('input, button, textarea');
              if (interactiveTarget) return;
              setInsertIndex(keywords.length);
            }}
            style={{
              height: listHeight || '284px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              alignContent: 'flex-start',
              gap: '4px',
              rowGap: '4px',
              columnGap: '6px',
              padding: '8px 10px',
            }}
          >
            {renderInsertInput(0)}
            {keywords.map((keyword, index) => (
              <Fragment key={`${keyword}-${index}`}>
                {!isReadOnly && index > 0 && (
                  <span
                    aria-hidden="true"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setInsertIndex(index);
                    }}
                    style={{
                      width: '0px',
                      minWidth: '0px',
                      padding: '0 4px',
                      margin: '0 -4px',
                      height: '22px',
                      display: 'inline-block',
                      cursor: 'text',
                      background: 'transparent',
                    }}
                  />
                )}
                <span
                  onMouseDown={(e) => {
                    if (isReadOnly) return;
                    if (e.target.closest('input, button')) return;
                    setInsertIndex(index + 1);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: getColorPalette(freqMap.get(keyword.toLowerCase())?.color).background,
                    border: `1px solid ${getColorPalette(freqMap.get(keyword.toLowerCase())?.color).border}`,
                    color: getColorPalette(freqMap.get(keyword.toLowerCase())?.color).text,
                    borderRadius: '9999px',
                    padding: '3px 8px 3px 10px',
                    fontSize: '13px',
                    fontWeight: 600,
                    maxWidth: '280px',
                  }}
                >
                  {(() => {
                    const isNewDraft = newKeywordDraft?.index === index;
                    const draftKeyword = normalizeKeyword(editedKeywords[index] ?? keyword);
                    const lookupKey = (draftKeyword || keyword).toLowerCase();
                    const match = freqMap.get(lookupKey);
                    const totalValue = Number(editedTotals[lookupKey]);
                    const resolvedTotal = Number.isFinite(totalValue)
                      ? totalValue
                      : isNewDraft
                        ? newKeywordDraft?.isManual
                          ? Number(newKeywordDraft?.total) || 1
                          : Number.isFinite(Number(match?.total))
                            ? Number(match.total)
                            : Number(newKeywordDraft?.total) || 1
                        : Number(match?.total);
                    const resolvedOccurance = Number.isFinite(Number(match?.occurance))
                      ? Number(match.occurance)
                      : 0;
                    const hasRatio = Number.isFinite(resolvedTotal);
                    const isKeywordEditing = !isReadOnly && (editingKeywordIndex === index || isNewDraft);
                    return (
                      <>
                        {isReadOnly ? (
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {keyword}
                          </span>
                        ) : !isKeywordEditing ? (
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              ignoreKeywordCommitRef.current = false;
                              setInsertIndex(null);
                              setEditingKeywordIndex(index);
                            }}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: getColorPalette(freqMap.get(keyword.toLowerCase())?.color).text,
                              fontWeight: 600,
                              fontSize: '13px',
                              lineHeight: 1.2,
                              padding: 0,
                              margin: 0,
                              maxWidth: '160px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              cursor: 'text',
                            }}
                          >
                            {keyword || t('mot clé')}
                          </button>
                        ) : (
                          <input
                            type="text"
                            autoFocus
                            value={editedKeywords[index] ?? keyword}
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) => updateKeywordDraft(index, e.target.value)}
                             onPaste={(e) => handleKeywordPaste(e, index)}
                            onBlur={() => commitKeywordRename(index)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                commitKeywordRename(index);
                                e.currentTarget.blur();
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                setEditedKeywords((prev) => ({ ...prev, [index]: keyword }));
                                setEditingKeywordIndex(null);
                              }
                            }}
                            style={{
                              border: '1px solid #d0d5dd',
                              outline: 'none',
                              background: '#ffffff',
                              backgroundColor: '#ffffff',
                              boxShadow: 'none',
                              WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              color: getColorPalette(freqMap.get(keyword.toLowerCase())?.color).text,
                              WebkitTextFillColor: getColorPalette(freqMap.get(keyword.toLowerCase())?.color).text,
                              caretColor: getColorPalette(freqMap.get(keyword.toLowerCase())?.color).text,
                              direction: 'ltr',
                              textAlign: 'center',
                              fontWeight: 600,
                              fontSize: '13px',
                              lineHeight: 1.2,
                              padding: '0 4px',
                              width: `${Math.max(1, String(editedKeywords[index] ?? keyword).length)}ch`,
                              minWidth: '10ch',
                              maxWidth: '220px',
                              borderRadius: '6px',
                            }}
                          />
                        )}
                        {hasRatio && (
                          <span
                            style={{
                              background: '#ffffff',
                              color: getColorPalette(freqMap.get(keyword.toLowerCase())?.color).text,
                              border: `1px solid ${getColorPalette(freqMap.get(keyword.toLowerCase())?.color).border}`,
                              borderRadius: '9999px',
                              padding: '1px 4px',
                              fontSize: '11px',
                              fontWeight: 700,
                              lineHeight: 1.3,
                              display: 'inline-flex',
                              alignItems: 'center',
                            }}
                          >
                            <span>{resolvedOccurance}/</span>
                            {isReadOnly ? (
                              <span>{resolvedTotal}</span>
                            ) : !isKeywordEditing ? (
                              <span>{resolvedTotal}</span>
                            ) : (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  fontWeight: 700,
                                  fontSize: '11px',
                                  lineHeight: 1.2,
                                  padding: 0,
                                  marginTop: '1px',
                                }}
                              >
                                <button
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    changeKeywordTotal(index, -1);
                                  }}
                                  style={{
                                    border: 'none',
                                    background: 'transparent',
                                    padding: 0,
                                    margin: 0,
                                    cursor: 'pointer',
                                    color: getColorPalette(freqMap.get(keyword.toLowerCase())?.color).text,
                                    fontWeight: 700,
                                    fontSize: '11px',
                                    lineHeight: 1,
                                  }}
                                >
                                  -
                                </button>
                                <span>{resolvedTotal}</span>
                                <button
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    changeKeywordTotal(index, 1);
                                  }}
                                  style={{
                                    border: 'none',
                                    background: 'transparent',
                                    padding: 0,
                                    margin: 0,
                                    cursor: 'pointer',
                                    color: getColorPalette(freqMap.get(keyword.toLowerCase())?.color).text,
                                    fontWeight: 700,
                                    fontSize: '11px',
                                    lineHeight: 1,
                                  }}
                                >
                                  +
                                </button>
                              </span>
                            )}
                          </span>
                        )}
                        {!isReadOnly && (
                          <button
                            type="button"
                            aria-label={t('Delete keyword')}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeKeyword(index);
                            }}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: getColorPalette(freqMap.get(keyword.toLowerCase())?.color).text,
                              fontSize: '11px',
                              lineHeight: 1,
                              padding: 0,
                              marginLeft: '1px',
                              cursor: 'pointer',
                              fontWeight: 600,
                            }}
                          >
                            x
                          </button>
                        )}
                      </>
                    );
                  })()}
                </span>
                {renderInsertInput(index + 1)}
              </Fragment>
            ))}
            {!isReadOnly && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addNewKeywordBubble();
                }}
                style={{
                  border: '1px dashed #d0d5dd',
                  background: '#ffffff',
                  borderRadius: '9999px',
                  padding: '3px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#344054',
                  cursor: newKeywordDraft ? 'not-allowed' : 'pointer',
                  opacity: newKeywordDraft ? 0.65 : 1,
                }}
                disabled={Boolean(newKeywordDraft)}
              >
                + mot clé
              </button>
            )}
            {!isReadOnly && keywords.some((keyword) => normalizeKeyword(keyword)) && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  clearKeywords();
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  borderRadius: '9999px',
                  padding: '3px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#b42318',
                  cursor: 'pointer',
                }}
              >
                {t('Tout supprimer')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
